import { useState, useEffect } from 'react';
import { Calendar, TrendingUp, TrendingDown, AlertTriangle, Users, Truck, Clock, ArrowLeft, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// StatCard component moved outside to avoid nested component definition
const StatCard = ({ title, value, icon: Icon, trend, color = 'emerald', onClick, clickable = false }) => {
  const colorClasses = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200'
  };

  const CardContent = (
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <p className="text-sm font-medium opacity-80">{title}</p>
        <p className="text-2xl sm:text-3xl font-bold mt-2">{value}</p>
        {trend !== undefined && (
          <div className="flex items-center gap-1 mt-2">
            {trend >= 0 ? (
              <TrendingUp className="w-4 h-4" />
            ) : (
              <TrendingDown className="w-4 h-4" />
            )}
            <span className="text-sm font-medium">
              {Math.abs(trend)}% vs last period
            </span>
          </div>
        )}
        {clickable && value > 0 && (
          <p className="text-xs opacity-60 mt-2">Click to view details</p>
        )}
      </div>
      <div className="p-3 bg-white rounded-lg shadow-sm">
        <Icon className="w-6 h-6" />
      </div>
    </div>
  );

  if (clickable && value > 0) {
    return (
      <button
        onClick={onClick}
        className={`w-full p-4 sm:p-6 rounded-xl border-2 ${colorClasses[color]} hover:shadow-lg transition-shadow cursor-pointer text-left`}
      >
        {CardContent}
      </button>
    );
  }

  return (
    <div className={`p-4 sm:p-6 rounded-xl border-2 ${colorClasses[color]}`}>
      {CardContent}
    </div>
  );
};

const DriverReports = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  
  // Initialize with today's date
  const today = new Date().toISOString().split('T')[0];
  const [filters, setFilters] = useState({
    driver_id: 'all',
    date_range: 'today',
    start_date: today,
    end_date: today
  });

  // Helper to set date range based on preset
  const setDateRange = (range) => {
    const today = new Date();
    let start, end;

    switch(range) {
      case 'today':
        start = end = today.toISOString().split('T')[0];
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        start = end = yesterday.toISOString().split('T')[0];
        break;
      case 'tomorrow':
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        start = end = tomorrow.toISOString().split('T')[0];
        break;
      case 'week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay()); // Sunday
        start = weekStart.toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      case 'month':
        start = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      case 'year':
        start = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      case 'custom':
        // For custom, just update the range selector
        setFilters(prev => ({
          ...prev,
          date_range: range
        }));
        return;
      default:
        return;
    }

    setFilters(prev => ({
      ...prev,
      date_range: range,
      start_date: start,
      end_date: end
    }));
  };
  const [analytics, setAnalytics] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLogs, setDetailLogs] = useState([]);
  const [detailType, setDetailType] = useState('');
  const [showFullLogDialog, setShowFullLogDialog] = useState(false);
  const [selectedFullLog, setSelectedFullLog] = useState(null);
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchDrivers();
    fetchAnalytics();
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [filters]);

  const fetchDrivers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/employees`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      // Filter for drivers (case-insensitive check on department and job_title)
      const driverList = data.filter(emp => {
        const dept = (emp.department || '').toLowerCase();
        const jobTitle = (emp.job_title || '').toLowerCase();
        return dept === 'drivers' || jobTitle === 'driver';
      });
      setDrivers(driverList);
    } catch (error) {
      console.error('Error fetching drivers:', error);
      toast.error('Failed to load drivers');
    }
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (filters.driver_id !== 'all') params.append('driver_id', filters.driver_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await fetch(`${BACKEND_URL}/api/driver-analytics?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch analytics');
      }

      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleTileClick = async (type) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (filters.driver_id !== 'all') params.append('driver_id', filters.driver_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      // Filter by type
      if (type === 'issues') params.append('show_issues', 'true');
      if (type === 'accidents') params.append('show_accidents', 'true');
      if (type === 'late_start' || type === 'late_finish') params.append('show_late', 'true');

      const response = await fetch(`${BACKEND_URL}/api/driver-logs-detailed?${params}&type=${type}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch detailed logs');
      }

      const data = await response.json();
      setDetailLogs(data.logs || []);
      setDetailType(type);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error fetching detailed logs:', error);
      toast.error('Failed to load detailed logs');
    }
  };

  const handleDownloadReport = async () => {
    // Use current page filters instead of separate dialog filters
    try {
      setDownloading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams({
        driver_id: filters.driver_id,
        start_date: filters.start_date,
        end_date: filters.end_date
      });

      const response = await fetch(`${BACKEND_URL}/api/driver-reports/download?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to generate report');
      }

      // Get the blob from response
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Generate filename
      const driverName = filters.driver_id === 'all' 
        ? 'All_Drivers' 
        : drivers.find(d => d.id === filters.driver_id)?.name?.replace(/\s+/g, '_') || 'Driver';
      a.download = `${driverName}_Report_${filters.start_date}_to_${filters.end_date}.pdf`;
      
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Report downloaded successfully');
      setShowDownloadDialog(false);
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error('Failed to download report');
    } finally {
      setDownloading(false);
    }
  };

  const handleViewFullLog = async (log) => {
    try {
      const token = localStorage.getItem('token');
      
      // If log has a log_id, fetch the complete log from backend
      if (log.log_id) {
        const response = await fetch(`${BACKEND_URL}/api/driver-logs/${log.log_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch full log');
        }

        const fullLog = await response.json();
        setSelectedFullLog(fullLog);
      } else {
        // Use the log data we already have
        setSelectedFullLog(log);
      }
      
      setShowFullLogDialog(true);
    } catch (error) {
      console.error('Error fetching full log:', error);
      toast.error('Failed to load full log details');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Loading analytics...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <Button onClick={() => navigate('/admin/dashboard')} variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button 
                onClick={() => setShowDownloadDialog(true)}
                className="bg-teal-600 hover:bg-teal-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Report
              </Button>
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Driver Reports & KPIs</h1>
              <p className="text-sm text-gray-500 mt-1">Performance analytics and compliance monitoring</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Filters</h2>
          <div className={`grid gap-4 ${filters.date_range === 'custom' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-6' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'}`}>
            {/* Driver Select */}
            <div>
              <label className="block text-sm font-medium mb-1">Driver</label>
              <select
                value={filters.driver_id}
                onChange={(e) => setFilters({...filters, driver_id: e.target.value})}
                className="w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="all">All Drivers</option>
                {drivers.map(driver => (
                  <option key={driver.id} value={driver.id}>
                    {driver.first_name && driver.last_name 
                      ? `${driver.first_name} ${driver.last_name}` 
                      : driver.name || driver.email}
                  </option>
                ))}
              </select>
            </div>

            {/* Date Range Preset */}
            <div>
              <label className="block text-sm font-medium mb-1">Date Range</label>
              <select
                value={filters.date_range}
                onChange={(e) => setDateRange(e.target.value)}
                className="w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="tomorrow">Tomorrow</option>
                <option value="week">Week to Date</option>
                <option value="month">Month to Date</option>
                <option value="year">Year to Date</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {/* Custom Date Range - Only show when Custom is selected */}
            {filters.date_range === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-1">From Date</label>
                  <input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => setFilters({...filters, start_date: e.target.value})}
                    className="w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">To Date</label>
                  <input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => setFilters({...filters, end_date: e.target.value})}
                    className="w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* KPI Cards */}
        {analytics && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <StatCard
                title="Starting Late"
                value={`${analytics.starting_late_percentage || 0}%`}
                icon={Clock}
                color="orange"
                clickable={true}
                onClick={() => handleTileClick('late_start')}
              />
              <StatCard
                title="Finishing Late"
                value={`${analytics.finishing_late_percentage || 0}%`}
                icon={Clock}
                color="orange"
                clickable={true}
                onClick={() => handleTileClick('late_finish')}
              />
              <StatCard
                title="Accidents Reported"
                value={analytics.accidents_count || 0}
                icon={AlertTriangle}
                color="red"
                clickable={true}
                onClick={() => handleTileClick('accidents')}
              />
              <StatCard
                title="Issues Reported"
                value={analytics.issues_count || 0}
                icon={AlertTriangle}
                color="blue"
                clickable={true}
                onClick={() => handleTileClick('issues')}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <StatCard
                title="Total Mileage"
                value={`${analytics.total_mileage?.toLocaleString() || 0} mi`}
                icon={Truck}
                color="emerald"
              />
              <StatCard
                title="Wash Compliance"
                value={`${analytics.wash_compliance_percentage || 0}%`}
                icon={Truck}
                color="blue"
              />
              <StatCard
                title="Driver Absences"
                value={`${analytics.absences_count || 0} days`}
                icon={Users}
                color="purple"
              />
            </div>

            {/* Detailed Tables */}
            <div className="space-y-6">
              {/* Non-Compliant Drivers Table */}
              {analytics.non_compliant_drivers && analytics.non_compliant_drivers.length > 0 && (
                <div className="bg-white rounded-xl shadow-md p-4 sm:p-6">
                  <h2 className="text-lg font-semibold mb-4 text-orange-700">
                    Wash Compliance Issues
                  </h2>
                  
                  {/* Desktop Table View */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-3 text-sm font-semibold">Driver</th>
                          <th className="text-left p-3 text-sm font-semibold">Last Inside Wash</th>
                          <th className="text-left p-3 text-sm font-semibold">Last Outside Wash</th>
                          <th className="text-left p-3 text-sm font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.non_compliant_drivers.map((driver, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="p-3 text-sm">{driver.driver_name}</td>
                            <td className="p-3 text-sm">{driver.last_inside_wash || 'Never'}</td>
                            <td className="p-3 text-sm">{driver.last_outside_wash || 'Never'}</td>
                            <td className="p-3">
                              <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-medium">
                                {driver.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="md:hidden space-y-3">
                    {analytics.non_compliant_drivers.map((driver, idx) => (
                      <div key={idx} className="border border-orange-200 rounded-lg p-4 bg-orange-50">
                        <div className="font-semibold text-gray-900 mb-3">{driver.driver_name}</div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Last Inside Wash:</span>
                            <span className="font-medium">{driver.last_inside_wash || 'Never'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Last Outside Wash:</span>
                            <span className="font-medium">{driver.last_outside_wash || 'Never'}</span>
                          </div>
                          <div className="pt-2 border-t border-orange-200">
                            <span className="inline-block px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-medium">
                              {driver.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Late Starts/Finishes Table */}
              {analytics.late_records && analytics.late_records.length > 0 && (
                <div className="bg-white rounded-xl shadow-md p-4 sm:p-6">
                  <h2 className="text-lg font-semibold mb-4 text-orange-700">
                    Late Starts & Finishes
                  </h2>
                  
                  {/* Desktop Table View */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-3 text-sm font-semibold">Driver</th>
                          <th className="text-left p-3 text-sm font-semibold">Date</th>
                          <th className="text-left p-3 text-sm font-semibold">Type</th>
                          <th className="text-left p-3 text-sm font-semibold">Scheduled</th>
                          <th className="text-left p-3 text-sm font-semibold">Actual</th>
                          <th className="text-left p-3 text-sm font-semibold">Delay</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.late_records.map((record, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="p-3 text-sm">{record.driver_name}</td>
                            <td className="p-3 text-sm">{record.date}</td>
                            <td className="p-3 text-sm">{record.type}</td>
                            <td className="p-3 text-sm">{record.scheduled_time}</td>
                            <td className="p-3 text-sm">{record.actual_time}</td>
                            <td className="p-3 text-sm text-orange-700 font-medium">
                              +{record.delay_minutes} min
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="md:hidden space-y-3">
                    {analytics.late_records.map((record, idx) => (
                      <div key={idx} className="border border-orange-200 rounded-lg p-4 bg-orange-50">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <div className="font-semibold text-gray-900">{record.driver_name}</div>
                            <div className="text-sm text-gray-600">{record.date}</div>
                          </div>
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
                            {record.type}
                          </span>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Scheduled:</span>
                            <span className="font-medium">{record.scheduled_time}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Actual:</span>
                            <span className="font-medium">{record.actual_time}</span>
                          </div>
                          <div className="flex justify-between pt-2 border-t border-orange-200">
                            <span className="text-gray-600">Delay:</span>
                            <span className="font-semibold text-orange-700">+{record.delay_minutes} min</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Detail View Section - Similar to Dashboard */}
        {showDetailModal && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-bold text-emerald-900">
                {detailType === 'late_start' && 'Late Start Details'}
                {detailType === 'late_finish' && 'Late Finish Details'}
                {detailType === 'accidents' && 'Accident Reports'}
                {detailType === 'issues' && 'Issue Reports'}
              </h3>
              <Button
                onClick={() => setShowDetailModal(false)}
                variant="outline"
                size="sm"
                className="text-xs"
              >
                Close
              </Button>
            </div>

            {detailLogs.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <AlertTriangle className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg">No logs found for this filter</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {detailLogs.map((log, idx) => (
                  <div 
                    key={idx} 
                    className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 border-l-4 border-emerald-500 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Driver</p>
                        <p className="text-sm font-semibold text-gray-900">{log.driver_name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Date</p>
                        <p className="text-sm text-gray-900">{log.date}</p>
                      </div>
                      {log.vehicle_registration && (
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Vehicle</p>
                          <p className="text-sm text-gray-900">{log.vehicle_registration}</p>
                        </div>
                      )}
                      {log.mileage !== undefined && (
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Mileage</p>
                          <p className="text-sm text-gray-900">{log.mileage} mi</p>
                        </div>
                      )}
                      {log.scheduled_time && (
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Scheduled</p>
                          <p className="text-sm text-gray-900">{log.scheduled_time}</p>
                        </div>
                      )}
                      {log.actual_time && (
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Actual</p>
                          <p className="text-sm text-gray-900">{log.actual_time}</p>
                        </div>
                      )}
                      {log.delay_minutes !== undefined && (
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Delay</p>
                          <p className="text-sm font-semibold text-orange-700">+{log.delay_minutes} min</p>
                        </div>
                      )}
                    </div>
                    {log.notes && (
                      <div className="mt-3 pt-3 border-t border-emerald-200">
                        <p className="text-xs text-gray-500 font-medium mb-1">Notes</p>
                        <p className="text-sm text-gray-700">{log.notes}</p>
                      </div>
                    )}
                    {log.accident_details && (
                      <div className="mt-3 pt-3 border-t border-red-200 bg-red-50 -mx-4 -mb-4 px-4 py-3 rounded-b-xl">
                        <p className="text-xs text-red-700 font-semibold mb-2">Accident Details</p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                          {log.accident_details.third_party_name && (
                            <div>
                              <span className="text-gray-600">3rd Party: </span>
                              <span className="font-medium">{log.accident_details.third_party_name}</span>
                            </div>
                          )}
                          {log.accident_details.contact_number && (
                            <div>
                              <span className="text-gray-600">Contact: </span>
                              <span className="font-medium">{log.accident_details.contact_number}</span>
                            </div>
                          )}
                          {log.accident_details.registration && (
                            <div>
                              <span className="text-gray-600">Reg: </span>
                              <span className="font-medium">{log.accident_details.registration}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* View Full Log Button */}
                    {log.log_id && (
                      <div className="mt-3 pt-3 border-t border-emerald-200">
                        <Button
                          onClick={() => handleViewFullLog(log)}
                          variant="outline"
                          size="sm"
                          className="w-full"
                        >
                          View Full Log
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Full Log Dialog */}
        {showFullLogDialog && selectedFullLog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
              {/* Dialog Header */}
              <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold">Complete Driver Log</h2>
                  <button
                    onClick={() => setShowFullLogDialog(false)}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Dialog Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
                <div className="space-y-6">
                  {/* Driver & Vehicle Info */}
                  <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                    <h3 className="font-bold text-emerald-900 mb-3">Driver & Vehicle Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Driver Name</p>
                        <p className="font-semibold">{selectedFullLog.driver_name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Vehicle Registration</p>
                        <p className="font-semibold">{selectedFullLog.vehicle_registration || selectedFullLog.vehicle_id || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Date</p>
                        <p className="font-semibold">{selectedFullLog.date || selectedFullLog.submitted_at?.split('T')[0] || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Status</p>
                        <p className="font-semibold capitalize">{selectedFullLog.status || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Mileage Info */}
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                    <h3 className="font-bold text-blue-900 mb-3">Mileage Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Start Mileage</p>
                        <p className="font-semibold">{selectedFullLog.start_mileage || 0} mi</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">End Mileage</p>
                        <p className="font-semibold">{selectedFullLog.end_mileage || 0} mi</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Total Distance</p>
                        <p className="font-semibold text-blue-700">
                          {(selectedFullLog.end_mileage || 0) - (selectedFullLog.start_mileage || 0)} mi
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Truck Wash Info */}
                  {(selectedFullLog.truck_washed_inside !== undefined || selectedFullLog.truck_washed_outside !== undefined) && (
                    <div className="bg-cyan-50 rounded-xl p-4 border border-cyan-200">
                      <h3 className="font-bold text-cyan-900 mb-3">Vehicle Wash Status</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${selectedFullLog.truck_washed_inside ? 'bg-green-500' : 'bg-gray-300'}`}>
                            {selectedFullLog.truck_washed_inside && (
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <span className="font-medium">Washed Inside</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${selectedFullLog.truck_washed_outside ? 'bg-green-500' : 'bg-gray-300'}`}>
                            {selectedFullLog.truck_washed_outside && (
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <span className="font-medium">Washed Outside</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {selectedFullLog.notes && (
                    <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
                      <h3 className="font-bold text-yellow-900 mb-3">Notes / Issues</h3>
                      <p className="text-gray-700 whitespace-pre-wrap">{selectedFullLog.notes}</p>
                    </div>
                  )}

                  {/* Accident Information */}
                  {selectedFullLog.had_accident && (
                    <div className="bg-red-50 rounded-xl p-4 border-2 border-red-300">
                      <h3 className="font-bold text-red-900 mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5" />
                        Accident Reported
                      </h3>
                      
                      {selectedFullLog.was_third_party_involved && (
                        <div className="space-y-3">
                          <p className="font-semibold text-red-800">3rd Party Involved</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {selectedFullLog.accident_third_party_name && (
                              <div>
                                <p className="text-sm text-gray-600">3rd Party Name</p>
                                <p className="font-semibold">{selectedFullLog.accident_third_party_name}</p>
                              </div>
                            )}
                            {selectedFullLog.accident_contact_number && (
                              <div>
                                <p className="text-sm text-gray-600">Contact Number</p>
                                <p className="font-semibold">{selectedFullLog.accident_contact_number}</p>
                              </div>
                            )}
                            {selectedFullLog.accident_registration && (
                              <div>
                                <p className="text-sm text-gray-600">Vehicle Registration</p>
                                <p className="font-semibold">{selectedFullLog.accident_registration}</p>
                              </div>
                            )}
                          </div>
                          
                          {/* Accident Images */}
                          {selectedFullLog.accident_images && selectedFullLog.accident_images.length > 0 && (
                            <div className="mt-4">
                              <p className="text-sm text-gray-600 mb-2">Accident Images ({selectedFullLog.accident_images.length})</p>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {selectedFullLog.accident_images.map((imgPath, idx) => (
                                  <a
                                    key={idx}
                                    href={`${BACKEND_URL}/${imgPath}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block"
                                  >
                                    <img
                                      src={`${BACKEND_URL}/${imgPath}`}
                                      alt={`Accident ${idx + 1}`}
                                      className="w-full h-32 object-cover rounded-lg border-2 border-red-200 hover:border-red-400 transition-colors cursor-pointer"
                                    />
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {!selectedFullLog.was_third_party_involved && (
                        <p className="text-red-700">No 3rd party involved</p>
                      )}
                    </div>
                  )}

                  {/* Clock In/Out Times */}
                  {(selectedFullLog.clock_in_timestamp || selectedFullLog.clock_out_timestamp) && (
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                      <h3 className="font-bold text-gray-900 mb-3">Clock Times</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        {selectedFullLog.clock_in_timestamp && (
                          <div>
                            <p className="text-gray-600">Clocked In</p>
                            <p className="font-medium">{new Date(selectedFullLog.clock_in_timestamp).toLocaleString()}</p>
                          </div>
                        )}
                        {selectedFullLog.clock_out_timestamp && (
                          <div>
                            <p className="text-gray-600">Clocked Out</p>
                            <p className="font-medium">{new Date(selectedFullLog.clock_out_timestamp).toLocaleString()}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Submission Time */}
                  {selectedFullLog.submitted_at && (
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                      <h3 className="font-bold text-gray-900 mb-3">Log Submitted</h3>
                      <div className="text-sm">
                        <p className="font-medium">{new Date(selectedFullLog.submitted_at).toLocaleString()}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Dialog Footer */}
              <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t">
                <Button
                  onClick={() => setShowFullLogDialog(false)}
                  variant="outline"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Download Report Dialog */}
        <Dialog open={showDownloadDialog} onOpenChange={setShowDownloadDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Download Driver KPI Report</DialogTitle>
              <DialogDescription>
                Generate a comprehensive PDF report using the current page filters.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                <p className="text-sm font-medium text-teal-900 mb-2">Current Filters:</p>
                <ul className="text-sm text-teal-800 space-y-1">
                  <li><strong>Driver:</strong> {filters.driver_id === 'all' ? 'All Drivers' : drivers.find(d => d.id === filters.driver_id)?.first_name + ' ' + drivers.find(d => d.id === filters.driver_id)?.last_name}</li>
                  <li><strong>Date Range:</strong> {filters.start_date} to {filters.end_date}</li>
                </ul>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800 font-medium mb-2">
                  <strong>Report will include:</strong>
                </p>
                <ul className="text-xs text-blue-700 space-y-1 ml-4">
                  <li>• KPI Summary (Late Starts/Finishes, Accidents, Issues, Mileage, Wash Compliance, Absences)</li>
                  <li>• Detailed tables for every late start and late finish</li>
                  <li>• Complete accident records with descriptions</li>
                  <li>• Full issue log with status and resolutions</li>
                  <li>• Mileage breakdown by date</li>
                  <li>• Wash compliance tracking (required/completed/missed)</li>
                  <li>• Absence records with types and notes</li>
                  {filters.driver_id === 'all' && <li className="font-bold mt-2">• Summary page + individual section per driver</li>}
                </ul>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowDownloadDialog(false)}
                disabled={downloading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDownloadReport}
                disabled={downloading}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {downloading ? (
                  <>
                    <Clock className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </div>
  );
};

export default DriverReports;
